import Bluebird from 'bluebird'
import { useState } from 'react'
import { useSelectedVariantContext } from '../components/step2/context/SelectedVariantContext'
import {
    actionAddImageLayer,
    actionSetImageCompressProgress,
    actionSetRunningImageCompress,
    updateLayerAttribute
} from '../components/step2/context/SelectedVariantContext/action'
import { getBlobFromUrl, getExtensionFromDataURL, handleUploadFile, handleUploadImageByLink } from '../components/step2/context/SelectedVariantContext/helper'
import { getLinksUploadArtwork, uploadDesignLibrary, uploadDesignLibraryTemplate, uploadTempArtwork } from '../services/ArtworkTemplateServices'
import { toaster } from '../shared/PFToast'
import { isBackofficeEnv } from '../helpers/isBackoffceEnv'
import { getLocalData, setLocalData } from '../services/StorageServices'

const PREFIX_LOG = '[mockup-generator]'
const LIMIT_IMAGE = 5


function dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}
async function convertBlobUrlToFile(blobUrl, defaultName = 'file') {
    const response = await fetch(blobUrl);
    const blob = await response.blob();

    const mimeType = blob.type || 'application/octet-stream';

    // MIME type to extension mapping
    const mimeToExt = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/gif': 'gif',
        'image/svg+xml': 'svg',
        'application/pdf': 'pdf',
        'text/plain': 'txt',
        'application/zip': 'zip',
        'application/json': 'json',
        'audio/mpeg': 'mp3',
        'video/mp4': 'mp4',
        // Add more as needed
    };

    const extension = mimeToExt[mimeType] || 'bin';
    const fileName = `${defaultName}.${extension}`;

    return new File([blob], fileName, { type: mimeType })
}

const useUploadFile = () => {
    const { dispatch } = useSelectedVariantContext()

    const [pending, setPending] = useState(false)

    const _handleProgress = (progress) => {
        dispatch(actionSetImageCompressProgress(progress))
    }

    const _randomPercent = (max, min) => {
        return Math.floor(Math.random() * (max - min) + min)
    }

    const _fetchUploadLink = async () => {
        try {
            const { success, data } = await getLinksUploadArtwork({ limit: 1 })
            if (!success) {
                return
            }
            return data[0]
        } catch (error) {
            throw error
        }
    }

    const uploadFile = async (file, isGetFromDrive = false) => {
        _handleProgress(0)
        setPending(true)
        try {
            if (1 === LIMIT_IMAGE) {
                toaster({
                    type: 'error',
                    message: `${LIMIT_IMAGE} images are allowed to upload`,
                })
                return
            }
            await Bluebird.delay(200)
            _handleProgress(_randomPercent(30, 10))
            await Bluebird.delay(200)
            _handleProgress(_randomPercent(60, 35))
            await Bluebird.delay(200)
            _handleProgress(_randomPercent(95, 75))
            let imageObj = null
            const imageUrl = await _fetchUploadLink()
            const link = imageUrl.split('?')[0]
            await uploadTempArtwork(imageUrl, file, '')
            if (isBackofficeEnv()) {
                const payload = {
                    link,
                    name: file.name,
                }
                const [resImageObj] = await Promise.all([
                    handleUploadFile(file, isGetFromDrive),
                    uploadDesignLibrary(payload),
                ])
                imageObj = {
                    ...resImageObj,
                    linkS3: link
                }
            } else {
                const designLibrary = getLocalData('designLibrary') || []
                imageObj = await handleUploadImageByLink(link, file.name)
                designLibrary.push(imageObj)
                setLocalData('designLibrary', designLibrary)
            }
            dispatch(actionAddImageLayer(imageObj))
        } catch (error) {
            toaster({
                type: 'error',
                message: error.message || 'An error occurred while reading the image',
            })
        } finally {
            setPending(false)
            _handleProgress(100)
            setTimeout(() => {
                dispatch(actionSetRunningImageCompress(false))
            }, 500)
        }
    }

    const uploadTemplate = async (file, data, callback) => {
        setPending(true)
        try {
            const imageUrl = await _fetchUploadLink()
            const previewLink = imageUrl.split('?')[0]
            await uploadTempArtwork(imageUrl, file, '')
            const mappedLayers = await Promise.all(data.layers.map(async (layer) => {
                // base64 image
                if (layer.src?.includes('data:image')) {
                    const imageUrl = await _fetchUploadLink()
                    const extension = getExtensionFromDataURL(layer.src)
                    const imageFile = dataURLtoFile(layer.src, Math.random().toString(36).substring(2, 15) + '.' + extension);
                    const link = imageUrl.split('?')[0]
                    await uploadTempArtwork(imageUrl, imageFile, '')
                    return {
                        ...layer,
                        src: link,
                        tinySrc: link,
                    }
                }
                // blob url
                if (layer.src?.includes('blob:')) {
                    const imageUrl = await _fetchUploadLink()
                    const imageFile = await convertBlobUrlToFile(layer.src, Math.random().toString(36).substring(2, 15));
                    const link = imageUrl.split('?')[0]
                    await uploadTempArtwork(imageUrl, imageFile)
                    return {
                        ...layer,
                        src: link,
                        tinySrc: link,
                    }
                }

                // merchize s3
                if (layer.src?.includes('merchize-artworks')) {
                    const objectUrl = await getBlobFromUrl(layer.src)
                    const imageUrl = await _fetchUploadLink()
                    const imageFile = await convertBlobUrlToFile(objectUrl, Math.random().toString(36).substring(2, 15));
                    const link = imageUrl.split('?')[0]
                    await uploadTempArtwork(imageUrl, imageFile)
                    return {
                        ...layer,
                        src: link,
                        tinySrc: link,
                    }
                }

                return layer
            }))
            const payload = {
                ...data,
                preview_image: previewLink,
                width: data.width || 1000,
                height: data.height || 1000,
                layers: data.layers ? mappedLayers.map(layer => ({
                    ...layer,
                    src: layer.linkS3 || layer.src,
                    tinySrc: layer.linkS3 || layer.tinySrc,
                })) : [],
                type: 'mockup'
            }
            if (isBackofficeEnv()) {
                const response = await uploadDesignLibraryTemplate(payload)
                if (!response.success) {
                    throw new Error(response.message)
                }
            } else {
                const designLibrary = getLocalData('designTemplate') || []
                designLibrary.push({
                    ...payload,
                    created_at: new Date().toISOString(),
                    _id: Math.random().toString(36).substring(2, 15),
                })
                setLocalData('designTemplate', designLibrary)
            }
            callback && callback(true)
        } catch (error) {
            if (!error || !error?.message) {
                throw new Error('Lost connection. Please try again.')
            }
            toaster({
                type: 'error',
                message: error.message?.includes('Network Error') ? 'Lost connection. Please try again.' : error.message,
            })
            callback && callback(false)
        } finally {
            setPending(false)
        }
    }


    const uploadFileWithUpdateLayer = async (dataUrl, file, targetLayer) => {
        if (!targetLayer) {
            toaster({ type: 'error', message: 'Target layer is not specified for update.' });
            return;
        }
        _handleProgress(0);
        setPending(true);
        try {
            await Bluebird.delay(200);
            _handleProgress(_randomPercent(30, 10));
            await Bluebird.delay(200);
            _handleProgress(_randomPercent(60, 35));
            await Bluebird.delay(200);
            _handleProgress(_randomPercent(95, 75));

            let imageObj = null;
            const imageUrl = await _fetchUploadLink();
            const link = imageUrl.split('?')[0];
            await uploadTempArtwork(imageUrl, file, '');

            if (isBackofficeEnv()) {
                const payload = { link, name: file.name };
                const [resImageObj] = await Promise.all([
                    handleUploadFile(file),
                    uploadDesignLibrary(payload),
                ]);
                imageObj = { ...resImageObj, linkS3: link };
            } else {
                const designLibrary = getLocalData('designLibrary') || [];
                imageObj = await handleUploadImageByLink(link, file.name);
                designLibrary.push(imageObj);
                setLocalData('designLibrary', designLibrary);
            }

            if (imageObj) {
                dispatch(
                    updateLayerAttribute({
                        ...targetLayer,
                        src: imageObj?.linkS3 || dataUrl,
                        tinySrc: imageObj?.linkS3 || dataUrl,
                    })
                );
                return true;
            }


        } catch (error) {
            toaster({
                type: 'error',
                message: error.message || 'An error occurred while updating the image',
            });
            return false;
        } finally {
            setPending(false);
            _handleProgress(100);
            setTimeout(() => {
                dispatch(actionSetRunningImageCompress(false));
            }, 500);
        }
    };

    return { uploadFile, uploadTemplate, uploadFileWithUpdateLayer, pending, _fetchUploadLink }
}

export default useUploadFile

